'use client'

import { Bot, User, AlertCircle, Wrench } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

import type { ChatMessageProps, ContentBlock } from './types'
import { formatDuration } from './utils'
import { ThinkingIndicator } from './ThinkingIndicator'
import { MessageActions } from './MessageActions'
import { TextBlock } from './blocks/TextBlock'
import { VisualizationBlock } from './blocks/VisualizationBlock'
import { InteractiveElementsBlock } from './blocks/InteractiveElementsBlock'

// ---------------------------------------------------------------------------
// Block Renderer
// ---------------------------------------------------------------------------

function renderBlock(
  block: ContentBlock,
  index: number,
  isActionResult?: boolean,
  onRespondToElement?: (elementId: string, actionMetadata: Record<string, unknown>) => Promise<void>,
  onSendMessage?: (content: string) => Promise<void>,
) {
  switch (block.type) {
    case 'text':
      return <TextBlock key={index} content={block.content} isActionResult={isActionResult} />
    case 'visualization':
      return (
        <VisualizationBlock
          key={index}
          payload={block.payload}
          textFallback={block.textFallback}
        />
      )
    case 'interactive-elements':
      return (
        <InteractiveElementsBlock
          key={index}
          elements={block.elements}
          onRespondToElement={onRespondToElement}
          onSendMessage={onSendMessage}
        />
      )
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// ChatMessage
// ---------------------------------------------------------------------------

/**
 * Single chat message with avatar, content blocks, action toolbar,
 * and status indicators (thinking, progress, tool executions, errors).
 *
 * - User messages: right-aligned bubble, constrained width
 * - Assistant messages: left-aligned, full-width prose, avatar
 */
export function ChatMessage({
  message,
  isThinking,
  toolCallCount,
  currentToolCalls,
  onCopy,
  isCopied,
  isStreaming,
  onRespondToElement,
  onSendMessage,
}: ChatMessageProps) {
  const isUser = message.role === 'user'
  const hasContent = message.rawContent.trim().length > 0 || message.blocks.some(b => b.type !== 'text')
  const showError = message.status === 'error' && message.error

  return (
    <div
      className={cn(
        'group flex w-full gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row',
        isUser ? 'mb-4' : 'mb-6',
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex size-8 shrink-0 select-none items-center justify-center rounded-full',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-background ring-1 ring-border',
        )}
      >
        {isUser ? <User className="size-4" /> : <Bot className="size-4" />}
      </div>

      {/* Content */}
      <div
        className={cn(
          'min-w-0 select-text',
          isUser
            ? 'max-w-[85%] rounded-3xl rounded-br-lg bg-primary px-5 py-2.5 text-primary-foreground'
            : 'max-w-none flex-1',
          message.isActionResult && !isUser &&
            'rounded-lg border border-green-200 bg-green-50/50 p-4 dark:border-green-800 dark:bg-green-950/20',
        )}
      >
        {/* Error state */}
        {showError ? (
          <Alert variant="destructive" className="border-destructive/30 bg-destructive/5">
            <AlertCircle className="size-4" />
            <AlertDescription>{message.error!.message}</AlertDescription>
          </Alert>
        ) : (
          <>
            {/* Content blocks */}
            {hasContent && message.blocks.map((block, i) => renderBlock(
              block,
              i,
              message.isActionResult,
              onRespondToElement,
              onSendMessage,
            ))}

            {/* Streaming cursor */}
            {isStreaming && message.status === 'streaming' && !isUser && (
              <span className="ml-0.5 inline-block h-4 w-[3px] animate-pulse bg-foreground align-text-bottom" />
            )}

            {/* Progress indicator */}
            {message.progress && message.status !== 'complete' && (
              <div className="mt-3 space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{message.progress.stage}</span>
                  {message.progress.percent !== undefined && (
                    <span>{Math.round(message.progress.percent)}%</span>
                  )}
                </div>
                {message.progress.percent !== undefined && (
                  <Progress value={message.progress.percent} className="h-1" />
                )}
              </div>
            )}

            {/* Tool executions */}
            {message.toolExecutions && message.toolExecutions.length > 0 && (
              <div className="mt-3 space-y-1">
                {message.toolExecutions.map((tool, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <Wrench className="size-3" />
                    <span className="font-mono">{tool.name}</span>
                    <span
                      className={cn(
                        'ml-auto',
                        tool.status === 'Completed' && 'text-green-600 dark:text-green-400',
                        tool.status === 'Failed' && 'text-red-600 dark:text-red-400',
                        tool.status === 'InProgress' && 'text-blue-600 dark:text-blue-400',
                      )}
                    >
                      {tool.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Thinking indicator */}
        {isThinking && (
          <ThinkingIndicator
            content={message.thinkingContent}
            toolCallCount={toolCallCount}
            currentToolCalls={currentToolCalls}
          />
        )}

        {/* Thinking duration */}
        {message.thinkingDuration && message.status !== 'error' && (
          <div className="mt-1 text-xs text-muted-foreground">
            Thought for {formatDuration(message.thinkingDuration)}
          </div>
        )}

        {/* Error badge on non-error messages (secondary indicator) */}
        {message.status === 'error' && !message.error && (
          <div className="mt-1 flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="size-3" />
            <span>Error occurred</span>
          </div>
        )}

        {/* Timestamp */}
        {message.timestamp && (
          <p
            className={cn(
              'mt-1 select-none text-xs',
              isUser ? 'text-primary-foreground/60' : 'text-muted-foreground',
            )}
          >
            {message.timestamp}
          </p>
        )}

        {/* Message actions (copy) - assistant only */}
        {!isUser && hasContent && message.status !== 'error' && (
          <MessageActions
            rawContent={message.rawContent}
            messageId={message.id}
            onCopy={onCopy}
            isCopied={isCopied}
          />
        )}
      </div>
    </div>
  )
}
